import Highlighter from 'react-highlight-words';
import { NavLink } from 'react-router-dom';
import { Button, renderResetCredentialsButton } from '../../../components';
import { URLS, USER_STATUSES } from '../../../constants/constants';

import { USER_ROLES_DICTIONARY } from '../../../dictionaries';
import { renderRecordValue } from '../../../utils';

import styles from './index.module.css';

const USER_STATUS_LABELS = {
  [USER_STATUSES.INVITED]: 'Invited (Pending Activation)',
  [USER_STATUSES.EXPIRED]: 'Invite Expired',
  [USER_STATUSES.DISABLED]: 'Inactive',
  [USER_STATUSES.ACTIVE]: 'Active',
};

export const renderButtons = ({
  isMe,
  isAdmin,
  isMHAdmin,
  isHOAdmin,
  isEnterpriseAdmin,
  managedRole,
  value,
  switchUserStatus,
  archieveUserRole,
  handleSendPassword,
  handleManageUser,
  handleResendInvitation,
  handleRescindInvitation,
  Navigation
}) => {
  if (isMe && isAdmin) {
    return [
      <div key={1} className={styles.tableButton}>
        <Button myClassName={styles.navigationBtn}>
          <NavLink
            to={{
              pathname: URLS.PREFERENCES,
              hash: 'personalInfo',
            }}>
            Manage {managedRole}
          </NavLink>
        </Button>
      </div>,
    ];
  } else if (value.status === USER_STATUSES.DISABLED && isAdmin) {
    return [
      ((isMHAdmin || isHOAdmin) && <div key={0} className={styles.restoreAccess}>
        <Button
          myClassName={styles.deleteBtn}
          onClick={archieveUserRole({ id: value.id, name: `${value.firstName} ${value.lastName}`, Navigation })}>
          Delete {managedRole}
        </Button>
      </div>
      ),
      <div key={1} className={styles.restoreAccess}>
        <Button
          myClassName={styles.navigationBtn}
          onClick={switchUserStatus({ status: { status: USER_STATUSES.ACTIVE, }, id: value.id, Navigation })}>
          Activate {managedRole}
        </Button>
      </div>,
    ];
  } else if (isAdmin && !isMe) {
    const invited = value.status === USER_STATUSES.INVITED;
    const expired = value.status === USER_STATUSES.EXPIRED;

    const showResendInvite = invited || expired || value.hasPassword === false;

    if (isEnterpriseAdmin) {
      return [
        renderResetCredentialsButton({
          invited: showResendInvite,
          value,
          handleSendPassword: handleSendPassword(value?.id),
          handleResendInvitation: handleResendInvitation(value?.id),
          resetButtonText: '(Send) Reset Password',
          resendButtonText: invited ? 'Resend Invitation' : 'Resend Invite',
        }),
        <div key={1} className={styles.tableButton}>
          <Button myClassName={styles.navigationBtn} onClick={handleManageUser(value.id, Navigation)}>
            Manage {managedRole}
          </Button>
        </div>,
      ];
    }

    return [
      invited
        ? (
          <div key={4} className={styles.tableButton}>
            <Button myClassName={styles.navigationBtn} onClick={handleRescindInvitation(value?.id, `${value?.firstName} ${value?.lastName}`)}>
             Cancel Invite
            </Button>
          </div>
        )
        : renderResetCredentialsButton({
          invited: showResendInvite,
          value,
          handleSendPassword: handleSendPassword(value?.id),
          handleResendInvitation: handleResendInvitation(value?.id),
          resetButtonText: '(Send) Reset Password',
          resendButtonText: 'Resend Invite',
        }),
      <div key={1} className={styles.tableButton}>
        <Button myClassName={styles.navigationBtn} onClick={handleManageUser(value.id, Navigation)}>
          Manage {managedRole}
        </Button>
      </div>,
    ];
  } else return [];
};

export const transformData = ({
  data,
  myEmail,
  switchUserStatus,
  archieveUserRole,
  handleManageUser,
  managedRole,
  isAdmin,
  isMHAdmin,
  isHOAdmin,
  isEnterpriseAdmin,
  handleSendPassword,
  handleResendInvitation,
  handleRescindInvitation,
  Navigation
}) => {
  return data?.map((value) => {
    const isMe = value.email === myEmail;

    return {
      key: value.id,
      name:
        value.firstName &&
        value.lastName &&
        renderRecordValue(`${value.firstName} ${value.lastName} ${isMe ? '(You)' : ''}`),
      email: value.email,
      type: value.role,
      status: USER_STATUS_LABELS[value.status] || value.status,
      buttons: renderButtons({
        isMe,
        isAdmin,
        isMHAdmin,
        isHOAdmin,
        isEnterpriseAdmin,
        managedRole,
        value,
        switchUserStatus,
        archieveUserRole,
        handleSendPassword,
        handleResendInvitation,
        handleRescindInvitation,
        handleManageUser,
        Navigation
      }),
    };
  });
};

export const renderColumns = (isAdmin, isMHAdmin, isHOAdmin, searchValue, highlighted) =>
  [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className={styles.cellValue}>
          {searchValue && highlighted ? (
            <Highlighter
              highlightStyle={{ backgroundColor: '# var(--ant-higlighter-color)', padding: 0 }}
              searchWords={[searchValue]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
          ) : (
            renderRecordValue(text)
          )}
        </div>
      ),
      width: 300,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (text) => <div className={styles.cellValue}>{text}</div>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '20%',
      render: (text) => (
        <div className={styles.cellValue}>
          {searchValue && highlighted ? (
            <Highlighter
              highlightStyle={{ backgroundColor: '# var(--ant-higlighter-color)', padding: 0 }}
              searchWords={[searchValue]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
          ) : (
            text
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render: (text) => <div className={styles.cellValue}>{USER_ROLES_DICTIONARY[text]}</div>,
    },
    {
      title: '',
      key: 'buttons',
      dataIndex: 'buttons',
      render: (buttons) => <div className={styles.btnPanel}>{buttons.map((Component) => Component)}</div>,
      width: 450,
    },
  ].filter((col) => (isAdmin ? col : col.key !== 'buttons'));
